import React from 'react';
import Modal from '../common/Modal';
import { toolsMetadata } from '../../shared/intents/toolsMetadata';

interface ToolsHelpModalProps {
  onClose: () => void;
}

const ToolsHelpModal: React.FC<ToolsHelpModalProps> = ({ onClose }) => {
  // Group tools by category (extract from id prefix)
  const grouped = toolsMetadata.reduce((acc, tool) => {
    const category = tool.id.split('.')[0];
    const label = category === 'marketing' ? 'Marketing & Content' 
      : category === 'finance' ? 'Finance' 
      : category === 'crm' ? 'CRM & Sales'
      : category === 'communications' ? 'Communications'
      : 'Other';
    
    if (!acc[label]) acc[label] = [];
    acc[label].push(tool);
    return acc;
  }, {} as Record<string, typeof toolsMetadata>);

  return (
    <Modal onClose={onClose} title="Available Tools & How to Use Them" size="large">
      <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>💡 Tip:</strong> Type any of the example phrases below into the chat to trigger the corresponding tool. 
            The AI will either open the tool for you or execute it directly.
          </p>
        </div>

        <div className="space-y-6">
          {Object.entries(grouped).map(([category, tools]) => (
            <div key={category}>
              <h3 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                {category}
              </h3>
              <div className="space-y-4">
                {tools.map((tool) => (
                  <div key={tool.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 transition">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{tool.name}</h4>
                      {tool.route && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                          Has UI
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{tool.description}</p>
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1.5">Example phrases:</p>
                      <div className="flex flex-wrap gap-2">
                        {tool.triggers.slice(0, 5).map((trigger, idx) => (
                          <code key={idx} className="text-xs px-2 py-1 bg-white border border-gray-300 rounded text-gray-800">
                            {trigger}
                          </code>
                        ))}
                        {tool.triggers.length > 5 && (
                          <span className="text-xs text-gray-500 self-center">
                            +{tool.triggers.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Total Tools Available:</strong> {toolsMetadata.length}
          </p>
          <p className="text-xs text-gray-600 mt-2">
            Can't find what you need? Just ask naturally in the chat and the AI will help guide you to the right tool.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default ToolsHelpModal;
